import type { ProceduralCharacter } from '../cars/CarProfile';

/**
 * ProceduralVoice — placeholder / fallback sound generation.
 *
 * Generates *loopable AudioBuffers* that look exactly like a recorded sample to
 * the rest of the engine: a loop synthesized at a given rpm goes through the same
 * RPMMixer / pitch / crossfade path as a real recording would. This means the
 * whole audio pipeline is exercised in Phase 1 and any missing real sample is
 * silently replaced at runtime (never a crash, never silence).
 */

export type LoopVariant = 'on' | 'off';

/** Tiny deterministic PRNG so the same rpm always yields the same loop. */
function makeRng(seed: number) {
  let s = (seed >>> 0) || 1;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function normalize(data: Float32Array, peak: number): void {
  let max = 0;
  for (let i = 0; i < data.length; i++) max = Math.max(max, Math.abs(data[i]));
  if (max === 0) return;
  const g = peak / max;
  for (let i = 0; i < data.length; i++) data[i] *= g;
}

/**
 * Synthesize a seamless engine loop at `rpm`.
 * 4-stroke firing frequency f0 = rpm/60 * cylinders/2; the loop length is an
 * integer number of half-order cycles so it wraps without a click.
 */
export function synthesizeEngineLoop(
  ctx: BaseAudioContext,
  rpm: number,
  ch: ProceduralCharacter,
  variant: LoopVariant,
): AudioBuffer {
  const sr = ctx.sampleRate;
  const on = variant === 'on';
  const f0 = (rpm / 60) * (ch.cylinders / 2);
  const fBase = f0 / 2; // half-order fundamental
  const targetDur = 1.5;
  const cycles = Math.max(2, Math.round(targetDur * fBase));
  const len = Math.round((cycles / fBase) * sr);
  const fb = (cycles * sr) / len; // exact base frequency → seamless
  const w0 = 2 * Math.PI * fb * 2; // firing angular freq

  const buf = ctx.createBuffer(1, len, sr);
  const out = buf.getChannelData(0);
  const rng = makeRng(Math.floor(rpm) * 31 + (on ? 1 : 7));

  const K = on ? 18 : 9;
  const tilt = on ? 1.05 - 0.45 * ch.brightness : 1.45 - 0.3 * ch.brightness;
  const phases: number[] = [];
  for (let k = 0; k <= K; k++) phases.push(rng() * Math.PI * 2);
  const amps: number[] = [0];
  for (let k = 1; k <= K; k++) amps.push(Math.pow(k, -tilt) * (k % 2 ? 1 : 0.82));
  const drive = on ? 2.0 + 1.6 * ch.brightness : 1.2;
  const halfOrder = ch.roughness * (on ? 0.5 : 0.3);
  const noiseAmt = ch.noise * (on ? 0.5 : 1.0);

  // harmonic part
  for (let n = 0; n < len; n++) {
    const t = n / sr;
    let s = 0;
    const wt = w0 * t;
    for (let k = 1; k <= K; k++) s += amps[k] * Math.sin(k * wt + phases[k]);
    s += halfOrder * Math.sin(2 * Math.PI * fb * t + phases[0]);
    s += halfOrder * 0.45 * Math.sin(2 * Math.PI * fb * 3 * t + phases[1]);
    out[n] = Math.tanh(drive * s * 0.33);
  }

  // noise part (low-passed, pulsed by firing), crossfaded at the seam
  const noise = new Float32Array(len);
  let lp = 0;
  const lpCoef = Math.exp((-2 * Math.PI * (on ? 2600 : 1800)) / sr);
  for (let n = 0; n < len; n++) {
    const white = rng() * 2 - 1;
    lp = lp * lpCoef + white * (1 - lpCoef);
    const puff = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(w0 * (n / sr)));
    noise[n] = lp * 3.2 * puff;
  }
  const seam = Math.min(1024, len >> 2);
  for (let i = 0; i < seam; i++) {
    const w = i / seam;
    const j = len - seam + i;
    noise[j] = noise[j] * (1 - w) + noise[i] * w;
  }
  for (let n = 0; n < len; n++) out[n] += noiseAmt * noise[n];

  normalize(out, on ? 0.85 : 0.62);
  return buf;
}

/** Short mechanical "clack" + thump for gear changes. */
export function synthesizeShift(ctx: BaseAudioContext, dir: 1 | -1): AudioBuffer {
  const sr = ctx.sampleRate;
  const dur = dir > 0 ? 0.075 : 0.09;
  const len = Math.round(dur * sr);
  const buf = ctx.createBuffer(1, len, sr);
  const out = buf.getChannelData(0);
  const rng = makeRng(dir > 0 ? 11 : 23);
  // resonant click around 1.8 kHz
  const f = dir > 0 ? 1900 : 1500;
  const r = Math.exp(-2 * Math.PI * 700 / sr);
  const a1 = 2 * r * Math.cos((2 * Math.PI * f) / sr);
  const a2 = -r * r;
  let y1 = 0;
  let y2 = 0;
  for (let n = 0; n < len; n++) {
    const t = n / sr;
    const exc = n < 40 ? (rng() * 2 - 1) : (rng() * 2 - 1) * Math.exp(-t * 90) * 0.3;
    const y = exc + a1 * y1 + a2 * y2;
    y2 = y1;
    y1 = y;
    const thump = Math.sin(2 * Math.PI * 110 * t) * Math.exp(-t * 45) * 0.8;
    out[n] = y * 0.35 * Math.exp(-t * 40) + thump;
  }
  normalize(out, 0.7);
  return buf;
}

/** Exhaust pop / crackle for lift-off backfires. */
export function synthesizeBackfire(ctx: BaseAudioContext, seed = 1): AudioBuffer {
  const sr = ctx.sampleRate;
  const dur = 0.22;
  const len = Math.round(dur * sr);
  const buf = ctx.createBuffer(1, len, sr);
  const out = buf.getChannelData(0);
  const rng = makeRng(seed * 97);
  let lp = 0;
  const lpCoef = Math.exp((-2 * Math.PI * 900) / sr);
  const pops = 2 + Math.floor(rng() * 3);
  const popAt: number[] = [];
  for (let i = 0; i < pops; i++) popAt.push(Math.floor(rng() * len * 0.7));
  for (let n = 0; n < len; n++) {
    const t = n / sr;
    let exc = (rng() * 2 - 1) * 0.25 * Math.exp(-t * 14);
    for (const p of popAt) if (n >= p && n < p + 24) exc += (rng() * 2 - 1) * 1.5;
    lp = lp * lpCoef + exc * (1 - lpCoef);
    out[n] = Math.tanh(lp * 4) * 0.9 + Math.sin(2 * Math.PI * 95 * t) * Math.exp(-t * 25) * 0.3;
  }
  normalize(out, 0.8);
  return buf;
}

/** Looping broadband noise used for wind / environment layers. */
export function synthesizeNoise(ctx: BaseAudioContext, seconds = 2): AudioBuffer {
  const sr = ctx.sampleRate;
  const len = Math.round(seconds * sr);
  const buf = ctx.createBuffer(1, len, sr);
  const out = buf.getChannelData(0);
  const rng = makeRng(4242);
  for (let n = 0; n < len; n++) out[n] = rng() * 2 - 1;
  const seam = 2048;
  for (let i = 0; i < seam; i++) {
    const w = i / seam;
    const j = len - seam + i;
    out[j] = out[j] * (1 - w) + out[i] * w;
  }
  return buf;
}
